import {
  Box,
  Flex,
  Text,
  Button,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Image,
  Input,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import Card from "components/Card/Card.js";
import React, { useState, useEffect, useMemo } from "react";
import { FaAngleRight, FaEye } from "react-icons/fa";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "Configuration/apiConfig";
import AgGridWrapper from "../../components/AgGrid/AgGridWrapper";
import Select, { components } from "react-select";
import { chakra } from "@chakra-ui/react";
import { FixedSizeList as List } from "react-window";

const CustomSelect = chakra(Select);
const { ValueContainer, Placeholder, MenuList } = components;


const CustomValueContainer = ({ children, ...props }) => {
  const { hasValue, isFocused } = props;
  return (
    <ValueContainer {...props}>
      {!hasValue && !isFocused && (
        <Placeholder {...props}>{props.selectProps.placeholder}</Placeholder>
      )}
      {React.Children.map(children, (child) =>
        child && child.type !== Placeholder ? child : null
      )}
    </ValueContainer>
  );
};

const VirtualizedMenuList = (props) => {
  const { options, children, maxHeight, getValue } = props;
  const height = 35;
  const [value] = getValue();
  const initialOffset = options.indexOf(value) * height;

  return (
    <List
      height={maxHeight}
      itemCount={children.length}
      itemSize={height}
      initialScrollOffset={initialOffset}
      width="100%"
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </List>
  );
};

export default function StoreReportDashboard() {
  const textColor = useColorModeValue("gray.700", "white");
  const [selectedStores, setSelectedStores] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [storeOptions, setStoreOptions] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [skuData, setSkuData] = useState([]);
  const [visibilityData, setVisibilityData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // SKU Details Modal (for Additional Data)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);

  // Visibility Eye Modal
  const [isEyeModalOpen, setIsEyeModalOpen] = useState(false);
  const [selectedVisibilityRow, setSelectedVisibilityRow] = useState(null);

  useEffect(() => {
    const cachedStores = localStorage.getItem("storeOptions");
    if (cachedStores) {
      setStoreOptions(JSON.parse(cachedStores));
      return;
    }

    const fetchStoreOptions = async () => {
      setStoresLoading(true);
      try {
        const response = await apiClient.get(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.STORE_DETAILS}`
        );
        if (Array.isArray(response.data)) {
          const mappedStores = response.data.map((store) => ({
            value: store.storeId.toString(),
            label: store.storeName,
          }));
          const allStores = [{ value: "all", label: "Select All" }, ...mappedStores];
          setStoreOptions(allStores);
          localStorage.setItem("storeOptions", JSON.stringify(allStores));
        } else {
          setError("Invalid store data received.");
        }
      } catch (err) {
        setError("Failed to fetch store options.");
        console.error("Store options fetch error:", err);
      } finally {
        setStoresLoading(false);
      }
    };

    fetchStoreOptions();
  }, []);

  const fetchReportData = async () => {
    if (!selectedStores.length || !selectedDate) {
      setError("Please select at least one store and a date.");
      setSkuData([]);
      setVisibilityData([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [year, month] = selectedDate.split("-");
      let storeIds;
      if (selectedStores.includes("all")) {
        storeIds = storeOptions
          .filter((s) => s.value !== "all")
          .map((s) => Number(s.value));
      } else {
        storeIds = selectedStores.map((s) => Number(s));
      }

      // Fetch SKU Data
      const skuResponse = await apiClient.post(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.STORE_REPORT.IMAGE_SUMMARY}`,
        { storeIds, month: Number(month), year: Number(year) }
      );

      const skuApiData = Array.isArray(skuResponse.data) ? skuResponse.data : [skuResponse.data];

      const transformedSkuData = await Promise.all(
        skuApiData.map(async (item, idx) => {
          const sno = item.sno ?? idx + 1;
          const storeId = item.store?.storeId ?? null;
          const storeName = item.store?.storeName || "Unknown Store";
          const imageName = item.fileName || null;
          const totalCount = item.totalCount ?? 0;

          let additionalData = [];
          let s3pathActualFile = "";
          let s3pathAnnotatedFile = "";

          try {
            if (storeId && imageName) {
              const body = {
                storeIds: [Number(storeId)],
                fileName: imageName,
                month: Number(month),
                year: Number(year),
              };

              const productResponse = await apiClient.post(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.STORE_REPORT.PRODUCT_SUMMARY}`,
                body
              );

              const data = Array.isArray(productResponse.data)
                ? productResponse.data
                : [productResponse.data];

              additionalData = data.map((productItem) => ({
                file: productItem.fileName ?? "N/A",
                company:
                  productItem.product?.productCompany?.productCompanyName ??
                  "Unknown",
                brand:
                  productItem.product?.productBrand?.productBrandName ??
                  "Unknown",
                type: productItem.product?.packType ?? "Unknown",
                size: productItem.product?.packSize ?? "Unknown",
                count: productItem.productCount ?? 0,
              }));

              const firstProduct = data[0];
              if (firstProduct?.s3pathActualFile) {
                const { data: actualUrl } = await apiClient.get(
                  `${API_CONFIG.SERVICE_URL}${API_CONFIG.ENDPOINTS.S3.PRESIGNED_URL}?fileName=${encodeURIComponent(
                    firstProduct.s3pathActualFile
                  )}&bucketName=${API_CONFIG.S3_BUCKETS.IMAGES}&operation=download`
                );
                s3pathActualFile = actualUrl;
              }

              if (firstProduct?.s3pathAnnotatedFile) {
                const { data: annotatedUrl } = await apiClient.get(
                  `${API_CONFIG.SERVICE_URL}${API_CONFIG.ENDPOINTS.S3.PRESIGNED_URL}?fileName=${encodeURIComponent(
                    firstProduct.s3pathAnnotatedFile
                  )}&bucketName=${API_CONFIG.S3_BUCKETS.IMAGES}&operation=download`
                );
                s3pathAnnotatedFile = annotatedUrl;
              }
            }
          } catch (err) {
            console.error("Failed to fetch product summary:", err);
            additionalData = [];
          }

          return {
            sno,
            storeId,
            storeName,
            imageName,
            count: totalCount.toString(),
            additionalData,
            s3pathActualFile,
            s3pathAnnotatedFile,
          };
        })
      );

      setSkuData(transformedSkuData);

      // Fetch Visibility Items Data
      const visibilityResponse = await apiClient.post(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.STORE_REPORT.VISIBILITY_ITEMS_STORE_WISE}`,
        { storeIds, month: Number(month), year: Number(year) }
      );

      const visibilityApiData = Array.isArray(visibilityResponse.data)
        ? visibilityResponse.data
        : [visibilityResponse.data];

      const transformedVisibilityData = await Promise.all(
        visibilityApiData.map(async (item, idx) => {
          const sno = item.sno ?? idx + 1;
          const storeId = item.store?.storeId ?? null;
          const storeName = item.store?.storeName || "Unknown Store";
          const imageName = item.fileName || null;
          const productText = item.productText || "N/A";
          const value = item.value?.toString() || "0.0";
          let s3pathActualFile = "";
          let s3pathAnnotatedFile = "";

          try {
            if (item.s3pathActualFile) {
              const { data: actualUrl } = await apiClient.get(
                `${API_CONFIG.SERVICE_URL}${API_CONFIG.ENDPOINTS.S3.PRESIGNED_URL}?fileName=${encodeURIComponent(
                  item.s3pathActualFile
                )}&bucketName=${API_CONFIG.S3_BUCKETS.IMAGES}&operation=download`
              );
              s3pathActualFile = actualUrl;
            }

            if (item.s3pathAnnotatedFile) {
              const { data: annotatedUrl } = await apiClient.get(
                `${API_CONFIG.SERVICE_URL}${API_CONFIG.ENDPOINTS.S3.PRESIGNED_URL}?fileName=${encodeURIComponent(
                  item.s3pathAnnotatedFile
                )}&bucketName=${API_CONFIG.S3_BUCKETS.IMAGES}&operation=download`
              );
              s3pathAnnotatedFile = annotatedUrl;
            }
          } catch (err) {
            console.error("Failed to fetch visibility item images:", err);
          }

          return {
            sno,
            storeId,
            storeName,
            imageName,
            productText,
            value,
            s3pathActualFile,
            s3pathAnnotatedFile,
          };
        })
      );

      setVisibilityData(transformedVisibilityData);
    } catch (err) {
      setError("Failed to fetch report data.");
      console.error("Report data fetch error:", err);
      setSkuData([]);
      setVisibilityData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (rowData) => {
    setSelectedRowData(rowData);
    setIsDetailsModalOpen(true);
  };

  const handleEyeClick = (rowData) => {
    setSelectedVisibilityRow(rowData);
    setIsEyeModalOpen(true);
  };

  const handleStoreChange = (newValue) => {
    const values = newValue.map((v) => v.value);
    if (values.includes("all")) {
      setSelectedStores(
        storeOptions.filter((s) => s.value !== "all").map((s) => s.value)
      );
    } else {
      setSelectedStores(values);
    }
  };

  const selectValue = useMemo(() => {
    const allStoresSelected =
      selectedStores.length ===
      storeOptions.filter((s) => s.value !== "all").length;
    if (allStoresSelected && storeOptions.length > 0) {
      return [{ value: "all", label: "Select All" }];
    }
    return storeOptions.filter((option) => selectedStores.includes(option.value));
  }, [selectedStores, storeOptions]);

  const skuColumnDefs = useMemo(
    () => [
      {
        headerName: "Additional",
        field: "expand",
        cellClass: "ag-numeric-cell",
        width: 130,
        cellRenderer: (params) => {
          const hasAdditionalData =
            params.data?.additionalData &&
            Array.isArray(params.data.additionalData) &&
            params.data.additionalData.length > 0;
          if (hasAdditionalData) {
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <div
                  onClick={() => handleViewDetails(params.data)}
                  style={{ cursor: "pointer" }}
                >
                  <FaAngleRight size={20} />
                </div>
              </div>
            );
          }
          return null;
        },
        sortable: false,
        filter: false,
      },
      { headerName: "S.No", field: "sno", width: 150, cellClass: "ag-numeric-cell", sortable: true, filter: true },
      {
        headerName: "Store Name",
        field: "storeName",
        flex: 1,
        minWidth: 80,
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "N/A",
      },
      {
        headerName: "Image Name",
        field: "imageName",
        flex: 1,
        minWidth: 80,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Count",
        field: "count",
        filter: "agNumberColumnFilter",
        cellClass: "ag-numeric-cell",
        width: 150,
        sortable: true,
      },
    ],
    []
  );

  const visibilityColumnDefs = useMemo(
    () => [
      { headerName: "S.No", field: "sno", width: 150, cellClass: "ag-numeric-cell", sortable: true, filter: true },
      {
        headerName: "Image Name",
        field: "imageName",
        flex: 1.5,
        minWidth: 120,
        sortable: true,
        filter: true,
        cellRenderer: (params) => (
          <Flex alignItems="center" gap="2">
            <Text>{params.value || "N/A"}</Text>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleEyeClick(params.data)}
            >
              <FaEye />
            </Button>
          </Flex>
        ),
      },
      {
        headerName: "Store Name",
        field: "storeName",
        flex: 1,
        minWidth: 120,
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "N/A",
      },
      
      {
        headerName: "Visibility Item",
        field: "productText",
        flex: 1.5,
        minWidth: 120,
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "N/A",
      },
      {
        headerName: "Value",
        field: "value",
        flex: 1,
        filter: "agNumberColumnFilter",
        cellClass: "ag-numeric-cell",
        width: 150,
        sortable: true,
        valueFormatter: (params) => params.value || "0.0",
      },
    ],
    []
  );

  const nestedColumnDefs = useMemo(
    () => [
      {
        headerName: "Company",
        field: "company",
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "Unknown",
      },
      {
        headerName: "Brand",
        field: "brand",
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "Unknown",
      },
      {
        headerName: "Pack Type",
        field: "type",
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "Unknown",
      },
      {
        headerName: "Pack Size",
        field: "size",
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "Unknown",
      },
      {
        headerName: "Count",
        field: "count",
        width:90,
        sortable: true,
        filter: "agNumberColumnFilter",
        cellClass: "ag-numeric-cell",
        valueFormatter: (params) => params.value?.toString() || "0",
      },
    ],
    []
  );

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <Card mb="20px" p="20px">
        <Flex justify="space-between" align="center" mb="20px">
          <Flex alignItems="center" gap="4">
            <CustomSelect
              isMulti
              options={storeOptions}
              value={selectValue}
              onChange={handleStoreChange}
              placeholder="Select Stores"
              width="300px"
              borderColor="gray.300"
              fontSize="sm"
              isLoading={storesLoading}
              components={{
                ValueContainer: CustomValueContainer,
                MenuList: VirtualizedMenuList,
              }}
            />
            <Input
              type="month"
              width="150px"
              borderColor="gray.300"
              fontSize="sm"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
            <Button
              sx={{ bg: "#C41E3A !important", color: "white !important" }}
              size="md"
              isDisabled={loading || storesLoading}
              onClick={fetchReportData}
            >
              Display
            </Button>
          </Flex>
        </Flex>

        <Tabs variant="enclosed" colorScheme="red">
          <TabList>
            <Tab>SKU Counts</Tab>
            <Tab>Visibility Items</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <AgGridWrapper
                rowData={skuData}
                columnDefs={skuColumnDefs}
                loading={loading}
                height="650px"
                noRowsMessage={error || "No data available"}
                extraGridProps={{ pagination: true, paginationPageSize: 8, headerHeight: 70 }}
              />
            </TabPanel>
            <TabPanel>
              <AgGridWrapper
                rowData={visibilityData}
                columnDefs={visibilityColumnDefs}
                loading={loading}
                height="650px"
                noRowsMessage={error || "No data available"}
                extraGridProps={{ pagination: true, paginationPageSize: 8, headerHeight: 70 }}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* SKU Additional Data Modal */}
        <Modal isOpen={isDetailsModalOpen} onClose={() => setIsDetailsModalOpen(false)} size="full">
          <ModalOverlay />
          <ModalContent
            p={0}
            m={0}
            maxW="100vw"
            minW="100vw"
            maxH="100vh"
            minH="100vh"
            borderRadius="0"
          >
            <ModalHeader>Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody overflow="auto">
              {selectedRowData && (
                <Box mb="20px">
                  <Flex justify="space-between" align="center">
                    <Text fontSize="lg" fontWeight="bold">
                      Store Name: {selectedRowData.storeName || "N/A"}
                    </Text>
                    <Text fontSize="lg" fontWeight="bold">
                      File Name: {selectedRowData.imageName || "N/A"}
                    </Text>
                  </Flex>
                </Box>
              )}
              {selectedRowData && (
                <Box>
                  <Flex direction="row" gap="20px" height="600px" alignItems="flex-start">
                    {selectedRowData.additionalData &&
                    Array.isArray(selectedRowData.additionalData) &&
                    selectedRowData.additionalData.length > 0 ? (
                      <Box height="100%" width="50%">
                        <AgGridWrapper
                          rowData={selectedRowData.additionalData}
                          columnDefs={nestedColumnDefs}
                          height="100%"
                          noRowsMessage="No additional data available"
                          extraGridProps={{ pagination: true, paginationPageSize: 8, headerHeight: 70 }}
                        />
                      </Box>
                    ) : (
                      <Box width="50%">
                        <Text fontSize="lg" fontWeight="bold">
                          Product Text: {selectedRowData.productText || "N/A"}
                        </Text>
                        <Text fontSize="lg" fontWeight="bold">
                          Value: {selectedRowData.value || "0.0"}
                        </Text>
                      </Box>
                    )}
                    <Flex direction="column" gap="10px" alignItems="center">
                      <Text fontSize="lg" fontWeight="bold">
                        Actual Image
                      </Text>
                      <Image
                        src={
                          selectedRowData.s3pathActualFile ||
                          "https://via.placeholder.com/300?text=No+Actual+Image"
                        }
                        alt="Actual"
                        maxW="800px"
                        maxH="700px"
                        w="120%"
                        h="700px"
                        objectFit="contain"
                        borderRadius="md"
                      />
                    </Flex>
                    <Flex direction="column" gap="10px" alignItems="center">
                      <Text fontSize="lg" fontWeight="bold">
                        Annotated Image
                      </Text>
                      <Image
                        src={
                          selectedRowData.s3pathAnnotatedFile ||
                          "https://via.placeholder.com/300?text=No+Annotated+Image"
                        }
                        alt="Annotated"
                        maxW="800px"
                        maxH="700px"
                        w="120%"
                        h="700px"
                        objectFit="contain"
                        borderRadius="md"
                      />
                    </Flex>
                  </Flex>
                </Box>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Visibility Eye Modal */}
        <Modal
          isOpen={isEyeModalOpen}
          onClose={() => setIsEyeModalOpen(false)}
          size="4xl"
        >
          <ModalOverlay />
          <ModalContent borderRadius="md">
            <ModalHeader>
              Image Details for {selectedVisibilityRow?.imageName || "N/A"}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedVisibilityRow && (
                <Box mb="20px">
                  <Flex justify="space-between" align="center">
                    <Text fontSize="lg" fontWeight="bold">
                      Store Name: {selectedVisibilityRow.storeName || "N/A"}
                    </Text>
                    <Text fontSize="lg" fontWeight="bold">
                      Product Text: {selectedVisibilityRow.productText || "N/A"}
                    </Text>
                  </Flex>
                </Box>
              )}
              <Flex
                direction="row"
                gap="30px"
                justify="center"
                alignItems="center"
                flexWrap="wrap"
              >
                {/* Actual Image */}
                <Flex direction="column" alignItems="center" gap="10px">
                  <Text fontSize="lg" fontWeight="bold">Actual Image</Text>
                  <Image
                    src={
                      selectedVisibilityRow?.s3pathActualFile ||
                      "https://via.placeholder.com/400x400?text=No+Actual+Image"
                    }
                    alt="Actual"
                    maxW="400px"
                    maxH="600px"
                    objectFit="contain"
                    borderRadius="md"
                  />
                </Flex>

                {/* Annotated Image */}
                <Flex direction="column" alignItems="center" gap="10px">
                  <Text fontSize="lg" fontWeight="bold">Annotated Image</Text>
                  <Image
                    src={
                      selectedVisibilityRow?.s3pathAnnotatedFile ||
                      "https://via.placeholder.com/400x400?text=No+Annotated+Image"
                    }
                    alt="Annotated"
                    maxW="400px"
                    maxH="600px"
                    objectFit="contain"
                    borderRadius="md"
                  />
                </Flex>
              </Flex>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Card>
    </Flex>
  );
}
